/**
 * Chat Redux Slice
 * ═══════════════════════════════════════════════════════════════════════════
 * SINGLE SOURCE OF TRUTH for the global Chat overlay (right-side Drawer).
 *
 * Why this exists (2026-09-10): ChatPanel was previously instantiated as a
 * LOCAL child component in 3 separate places (PlatformHeader, Speed Dial,
 * Meeting Master), each with its own local `useState` open/close flag. When
 * triggered from inside another overlay (Phone Tool dialog, Meeting Master),
 * the locally-nested Drawer got visually buried behind the enclosing
 * Dialog/Modal (MUI Drawer default zIndex 1200 < Dialog default zIndex 1300).
 *
 * Fix: ChatPanel is now mounted ONCE at the app shell level (PlatformHeader)
 * with an elevated zIndex. Every other component just dispatches
 * `openChat(filterContext)` / `closeChat()` via the `useChat()` hook instead
 * of rendering its own `<ChatPanel>` instance.
 *
 * Pattern: PlatformHeader → Redux → Components (see
 * docs/agent-contracts/PATTERN_REDUX_AUTH_DEPENDENCY_STANDARD.md,
 * "Future Features: Real-Time Collaboration Pattern" section).
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ChatFilterContext {
    organization_uuid?: string; // Meeting/org context
    pfuser_uuid?: string; // Direct chat context
}

interface ChatState {
    isOpen: boolean;
    filterContext: ChatFilterContext | null;
}

const initialState: ChatState = {
    isOpen: false,
    filterContext: null,
};

const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        openChat(state, action: PayloadAction<ChatFilterContext | undefined>) {
            state.isOpen = true;
            state.filterContext = action.payload ?? null;
        },
        closeChat(state) {
            state.isOpen = false;
        },
    },
});

export const { openChat, closeChat } = chatSlice.actions;
export default chatSlice.reducer;
